// ═══════════════════════════════════════════════════════════════════════════
//  ChartIndicators.ts — Jaiswal Capital SuperChart
//  Extracted from SuperChart.tsx (v34)
//
//  TradingView-style indicator registry:
//    • FAVORITES / PERSONAL — MAMTA AI custom scripts
//    • MY SCRIPTS           — Pine Script strategies (for the browser UI)
//    • BUILT-IN / TECHNICALS— EMA, SMA, RSI, MACD, BB, Supertrend, etc.
//
//  Usage in SuperChart.tsx:
//    import {
//      INDICATOR_META, INDICATOR_KEYS, IndicatorKey, IndicatorConfig,
//      IndicatorConfigs, IndicatorMeta, buildDefaultConfigs, TA,
//      TV_INDICATOR_REGISTRY, getSrc,
//    } from './ChartIndicators';
// ═══════════════════════════════════════════════════════════════════════════

// ─── Re-export the Candle type shape needed by TA functions ─────────────────
// (Keep Candle defined in SuperChart.tsx and import it here if preferred;
//  or duplicate the minimal shape. Duplicating avoids a circular dep.)
export interface Candle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  symbol?: string;
}

// ─── Indicator Keys ──────────────────────────────────────────────────────────
export type IndicatorKey =
  | 'ema1' | 'ema2' | 'ema3' | 'ema4'
  | 'sma1' | 'sma2'
  | 'vwap' | 'bb' | 'rsi' | 'macd' | 'volume'
  | 'supertrend' | 'stoch' | 'atr_ind' | 'pivots' | 'williamsR' | 'cci' | 'psar'
  | 'orderblock';

// ─── Indicator Configuration ─────────────────────────────────────────────────
export interface IndicatorConfig {
  enabled: boolean;
  visible: boolean;
  color: string;
  thickness: number;
  period?: number;
  stdDev?: number;
  fastPeriod?: number;
  slowPeriod?: number;
  signalPeriod?: number;
  signalColor?: string;
  histColor?: string;
  upColor?: string;
  downColor?: string;
  opacity?: number;
  overbought?: number;
  oversold?: number;
  multiplier?: number;
  stochKPeriod?: number;
  stochKSmooth?: number;
  stochDSmooth?: number;
  psarStep?: number;
  psarMax?: number;
  // Extended TV-style fields
  source?: 'close' | 'open' | 'high' | 'low' | 'hl2' | 'hlc3';
  offset?: number;
  // ── Order Block Detector (LuxAlgo-parity) ──
  obPivotLen?: number;            // Volume Pivot Length
  obBullCount?: number;           // how many bullish OBs to keep
  obBearCount?: number;
  obBullColor?: string;           // active bull box/border
  obBearColor?: string;
  obMitColor?: string;            // mitigated box colour (grey)
  obAvgStyle?: 'solid' | 'dashed' | 'dotted';
  obAvgWidth?: number;
  obMitigation?: 'Wick' | 'Close';
  obShowBoxes?: boolean;
  obShowLines?: boolean;          // average lines
  // ── Pivot Points (TradingView-parity) ──
  pivotType?: 'Traditional' | 'Fibonacci' | 'Woodie' | 'Classic' | 'Camarilla' | 'DM';
  pivotTf?: 'Auto' | 'Daily' | 'Weekly' | 'Monthly';
  pivotsBack?: number;            // number of historical pivot sets to draw
  pivotDailyBased?: boolean;      // use daily-based values on intraday charts
  pivotShowLabels?: boolean;      // pane labels: P/R1/S1…
  pivotShowPrices?: boolean;      // pane labels: price values
  pivotLabelPos?: 'Left' | 'Right';
  // per-level toggles + colours stored FLAT for easy merge: pivotOn_P, pivotColor_R3 …
  [k: `pivotOn_${string}`]: boolean | undefined;
  [k: `pivotColor_${string}`]: string | undefined;
  lineType?: 'solid' | 'dashed' | 'dotted';
  smoothType?: 'None' | 'EMA' | 'SMA';
  smoothLen?: number;
  showLabel?: boolean;
  showInStatus?: boolean;
  showInputsInStatus?: boolean;
}

// ─── Indicator Metadata ──────────────────────────────────────────────────────
export interface IndicatorMeta {
  id: IndicatorKey;
  name: string;
  shortName: (cfg: IndicatorConfig) => string;
  category: 'trend' | 'momentum' | 'volatility' | 'volume';
  pane: 'main' | 'sub';
  defaults: IndicatorConfig;
  hasField: {
    period?: boolean;
    stdDev?: boolean;
    macd?: boolean;
    rsi?: boolean;
    volume?: boolean;
  };
}

export type IndicatorConfigs = Record<IndicatorKey, IndicatorConfig>;

// ─── Default extended TV fields (shared base) ────────────────────────────────
const IND_DEF_EXT = {
  source: 'close' as const,
  offset: 0,
  lineType: 'solid' as const,
  smoothType: 'None' as const,
  smoothLen: 14,
  showLabel: true,
  showInStatus: true,
  showInputsInStatus: true,
};

// ═══════════════════════════════════════════════════════════════════════════
//  INDICATOR METADATA REGISTRY
// ═══════════════════════════════════════════════════════════════════════════
export const INDICATOR_META: Record<IndicatorKey, IndicatorMeta> = {
  ema1: {
    id: 'ema1', name: 'EMA', category: 'trend', pane: 'main',
    shortName: (c) => `EMA ${c.period}`,
    defaults: { enabled: true, visible: true, color: '#2962ff', thickness: 2, period: 5, ...IND_DEF_EXT },
    hasField: { period: true },
  },
  ema2: {
    // God 2026-07-16: the 13-EMA is OFF by default — the Vega DNA is a pure
    // 5 / 50 / 200 stack now (200 trend + 50 SL/side + 5 trigger). The slot is
    // kept (user can re-enable a custom period) but no 13-line "kachra" on the
    // default chart. Storage key bumped to v34 so stale saved configs (that had
    // this enabled) are discarded and rebuilt clean.
    id: 'ema2', name: 'EMA', category: 'trend', pane: 'main',
    shortName: (c) => `EMA ${c.period}`,
    defaults: { enabled: false, visible: true, color: '#fbbf24', thickness: 2, period: 13, ...IND_DEF_EXT },
    hasField: { period: true },
  },
  ema3: {
    id: 'ema3', name: 'EMA', category: 'trend', pane: 'main',
    shortName: (c) => `EMA ${c.period}`,
    defaults: { enabled: true, visible: true, color: '#9c27b0', thickness: 2, period: 50, ...IND_DEF_EXT },
    hasField: { period: true },
  },
  ema4: {
    id: 'ema4', name: 'EMA', category: 'trend', pane: 'main',
    shortName: (c) => `EMA ${c.period}`,
    defaults: { enabled: true, visible: true, color: '#ef5350', thickness: 3, period: 200, ...IND_DEF_EXT },
    hasField: { period: true },
  },
  sma1: {
    id: 'sma1', name: 'SMA', category: 'trend', pane: 'main',
    shortName: (c) => `SMA ${c.period}`,
    defaults: { enabled: false, visible: true, color: '#03a9f4', thickness: 2, period: 20, ...IND_DEF_EXT },
    hasField: { period: true },
  },
  sma2: {
    id: 'sma2', name: 'SMA', category: 'trend', pane: 'main',
    shortName: (c) => `SMA ${c.period}`,
    defaults: { enabled: false, visible: true, color: '#ff9800', thickness: 2, period: 50, ...IND_DEF_EXT },
    hasField: { period: true },
  },
  vwap: {
    id: 'vwap', name: 'VWAP', category: 'volume', pane: 'main',
    shortName: () => 'VWAP',
    defaults: {
      enabled: false, visible: true, color: '#fbbf24', thickness: 2,
      lineType: 'solid', showLabel: true, showInStatus: true,
    },
    hasField: {},
  },
  bb: {
    id: 'bb', name: 'Bollinger Bands', category: 'volatility', pane: 'main',
    shortName: (c) => `BB ${c.period},${c.stdDev}`,
    defaults: {
      enabled: false, visible: true, color: '#38bdf8', thickness: 1, period: 20, stdDev: 2,
      signalColor: 'rgba(255,255,255,0.5)', opacity: 6,
      source: 'close' as const, lineType: 'solid' as const, showLabel: true, showInStatus: true,
    },
    hasField: { period: true, stdDev: true },
  },
  rsi: {
    id: 'rsi', name: 'RSI', category: 'momentum', pane: 'sub',
    shortName: (c) => `RSI ${c.period}`,
    defaults: {
      enabled: false, visible: true, color: '#a78bfa', thickness: 2, period: 14,
      overbought: 70, oversold: 30,
      source: 'close' as const, lineType: 'solid' as const, showLabel: true, showInStatus: true,
    },
    hasField: { period: true, rsi: true },
  },
  macd: {
    id: 'macd', name: 'MACD', category: 'momentum', pane: 'sub',
    shortName: (c) => `MACD ${c.fastPeriod},${c.slowPeriod},${c.signalPeriod}`,
    defaults: {
      enabled: false, visible: true, color: '#2962ff', thickness: 2,
      fastPeriod: 12, slowPeriod: 26, signalPeriod: 9,
      signalColor: '#fbbf24', histColor: '#26a69a',
      source: 'close' as const, lineType: 'solid' as const, showLabel: true, showInStatus: true,
    },
    hasField: { macd: true },
  },
  volume: {
    id: 'volume', name: 'Volume', category: 'volume', pane: 'sub',
    shortName: () => 'Vol',
    defaults: {
      enabled: true, visible: true, color: '#26a69a', thickness: 1,
      upColor: '#26a69a', downColor: '#ef5350', opacity: 50,
      showLabel: true, showInStatus: true,
    },
    hasField: { volume: true },
  },
  supertrend: {
    id: 'supertrend', name: 'Supertrend', category: 'trend', pane: 'main',
    shortName: (c) => `ST ${c.period},${c.multiplier}`,
    defaults: {
      enabled: false, visible: true, color: '#26a69a', thickness: 2,
      period: 10, multiplier: 3, upColor: '#26a69a', downColor: '#ef5350',
      showLabel: true, showInStatus: true,
    },
    hasField: { period: true },
  },
  stoch: {
    id: 'stoch', name: 'Stochastic', category: 'momentum', pane: 'sub',
    shortName: (c) => `Stoch ${c.stochKPeriod},${c.stochKSmooth},${c.stochDSmooth}`,
    defaults: {
      enabled: false, visible: true, color: '#2962ff', thickness: 2,
      stochKPeriod: 14, stochKSmooth: 3, stochDSmooth: 3,
      signalColor: '#ef5350', overbought: 80, oversold: 20,
      showLabel: true, showInStatus: true,
    },
    hasField: {},
  },
  atr_ind: {
    id: 'atr_ind', name: 'ATR', category: 'volatility', pane: 'sub',
    shortName: (c) => `ATR ${c.period}`,
    defaults: {
      enabled: false, visible: true, color: '#f59e0b', thickness: 2, period: 14,
      showLabel: true, showInStatus: true,
    },
    hasField: { period: true },
  },
  orderblock: {
    id: 'orderblock', name: 'Order Block Detector', category: 'trend', pane: 'main',
    shortName: (c) => `OB [Lux] ${c.obPivotLen ?? 12} ${c.obBullCount ?? 2} ${c.obBearCount ?? 2}`,
    defaults: {
      enabled: false, visible: true, color: '#089981', thickness: 1,
      upColor: '#089981', downColor: '#f23645', showLabel: true, showInStatus: true,
      // LuxAlgo-parity inputs
      obPivotLen: 12, obBullCount: 2, obBearCount: 2,
      obBullColor: '#089981', obBearColor: '#f23645', obMitColor: '#787b86',
      obAvgStyle: 'solid', obAvgWidth: 1, obMitigation: 'Wick',
      obShowBoxes: true, obShowLines: true,
    } as IndicatorConfig,
    hasField: {},
  },
  pivots: {
    id: 'pivots', name: 'Pivot Points', category: 'trend', pane: 'main',
    shortName: (c) => `Pivots ${c.pivotType || 'Fibonacci'} ${c.pivotTf || 'Auto'}`,
    defaults: {
      enabled: true, visible: true, color: '#787b86', thickness: 1,     // God 30-Oct: pivots ON everywhere, first open too
      upColor: '#26a69a', downColor: '#ef5350', showLabel: true, showInStatus: true,
      // TV-parity inputs
      pivotType: 'Fibonacci', pivotTf: 'Auto', pivotsBack: 15, pivotDailyBased: true,   // God 2026-10-30: pivots are Fibonacci
      pivotShowLabels: true, pivotShowPrices: true, pivotLabelPos: 'Left',
      pivotOn_P: true,
      pivotOn_S1: true, pivotOn_S2: true, pivotOn_S3: true, pivotOn_S4: false, pivotOn_S5: false,
      pivotOn_R1: true, pivotOn_R2: true, pivotOn_R3: true, pivotOn_R4: false, pivotOn_R5: false,
      pivotColor_P: '#787b86',
      pivotColor_S1: '#4caf50', pivotColor_S2: '#4caf50', pivotColor_S3: '#4caf50',
      pivotColor_S4: '#4caf50', pivotColor_S5: '#4caf50',
      pivotColor_R1: '#f23645', pivotColor_R2: '#f23645', pivotColor_R3: '#f23645',
      pivotColor_R4: '#f23645', pivotColor_R5: '#f23645',
    } as IndicatorConfig,
    hasField: {},
  },
  williamsR: {
    id: 'williamsR', name: 'Williams %R', category: 'momentum', pane: 'sub',
    shortName: (c) => `%R ${c.period}`,
    defaults: {
      enabled: false, visible: true, color: '#06b6d4', thickness: 2,
      period: 14, overbought: -20, oversold: -80, showLabel: true, showInStatus: true,
    },
    hasField: { period: true },
  },
  cci: {
    id: 'cci', name: 'CCI', category: 'momentum', pane: 'sub',
    shortName: (c) => `CCI ${c.period}`,
    defaults: {
      enabled: false, visible: true, color: '#f97316', thickness: 2,
      period: 20, overbought: 100, oversold: -100, showLabel: true, showInStatus: true,
    },
    hasField: { period: true },
  },
  psar: {
    id: 'psar', name: 'Parabolic SAR', category: 'trend', pane: 'main',
    shortName: () => 'PSAR',
    defaults: {
      enabled: false, visible: true, color: '#fb923c', thickness: 2,
      psarStep: 0.02, psarMax: 0.2, upColor: '#26a69a', downColor: '#ef5350',
      showLabel: true, showInStatus: true,
    },
    hasField: {},
  },
};

export const INDICATOR_KEYS: IndicatorKey[] = Object.keys(INDICATOR_META) as IndicatorKey[];

export const buildDefaultConfigs = (): IndicatorConfigs => {
  const out: any = {};
  for (const k of INDICATOR_KEYS) out[k] = { ...INDICATOR_META[k].defaults };
  return out as IndicatorConfigs;
};

// ═══════════════════════════════════════════════════════════════════════════
//  TV-STYLE INDICATOR BROWSER REGISTRY
//  Shown in the "Indicators" modal — three categories matching TradingView
// ═══════════════════════════════════════════════════════════════════════════

export interface TVRegistrySection {
  label: string;
  icon: string;                   // emoji / icon hint for UI
  items: TVRegistryItem[];
}

export interface TVRegistryItem {
  id: string;
  name: string;
  description: string;
  type: 'builtin' | 'script' | 'favorite';
  indicatorKey?: IndicatorKey;    // undefined for Pine Script items
  color?: string;                 // accent color for the browser chip
  isNew?: boolean;
}

export const TV_INDICATOR_REGISTRY: TVRegistrySection[] = [
  // ── 1. Favorites / Personal ────────────────────────────────────────────
  {
    label: 'Favorites',
    icon: '⭐',
    items: [
      {
        id: 'fav_ema_ribbon',
        name: 'EMA Ribbon (5/13/50/200)',
        description: 'Four EMAs — Jaiswal Capital core setup',
        type: 'favorite',
        color: '#fbbf24',
      },
      {
        id: 'fav_vwap_ema',
        name: 'VWAP + EMA Confluence',
        description: 'VWAP combined with EMA 5 & 13 for intraday scalping',
        type: 'favorite',
        color: '#22d3ee',
      },
      {
        id: 'fav_supertrend_rsi',
        name: 'Supertrend + RSI',
        description: 'Trend filter with RSI overbought/oversold gate',
        type: 'favorite',
        color: '#a78bfa',
      },
    ],
  },

  // ── 2. My Scripts (MAMTA AI Pine Strategies) ───────────────────────────
  {
    label: 'My Scripts',
    icon: '🤖',
    items: [
      {
        id: 'mamta_veg',
        name: 'MAMTA AI RUDRA VEGA (GOD DNA SNIPER) — v41',
        description: 'Pinpoint 5×50 U-turn entry · 200-EMA trend confirmation · 50-EMA structural SL. Pure 5/50/200 stack (no 13). Dual-window CE/PE.',
        type: 'script',
        color: '#26a69a',
        isNew: true,
      },
      {
        id: 'mamta_shool',
        name: 'MAMTA AI — Rudra Shool [Trend Follower]',
        description: 'Supertrend + MACD + Stochastic triple-confirmation trend system.',
        type: 'script',
        color: '#ef5350',
      },
      {
        id: 'mamta_sthambh',
        name: 'MAMTA AI — Rudra Sthambh [Positional]',
        description: 'ADX + EMA 50/200 breakout strategy for swing/positional trades.',
        type: 'script',
        color: '#818cf8',
      },
      {
        id: 'mamta_smc',
        name: 'MAMTA AI — SMC Liquidity Engine',
        description: 'Smart Money Concepts — Fair Value Gaps + Order Block detection.',
        type: 'script',
        color: '#f59e0b',
        isNew: true,
      },
    ],
  },

  // ── 3. Built-in / Technicals ───────────────────────────────────────────
  {
    label: 'Built-in / Technicals',
    icon: '📊',
    items: [
      // Trend
      {
        id: 'builtin_ema1',   name: 'EMA (5)',          description: 'Exponential Moving Average — fast signal line',
        type: 'builtin', indicatorKey: 'ema1', color: '#2962ff',
      },
      {
        id: 'builtin_ema2',   name: 'EMA (13)',         description: 'Exponential Moving Average — medium signal',
        type: 'builtin', indicatorKey: 'ema2', color: '#fbbf24',
      },
      {
        id: 'builtin_ema3',   name: 'EMA (50)',         description: 'Exponential Moving Average — trend filter',
        type: 'builtin', indicatorKey: 'ema3', color: '#9c27b0',
      },
      {
        id: 'builtin_ema4',   name: 'EMA (200)',        description: 'Exponential Moving Average — long-term bias',
        type: 'builtin', indicatorKey: 'ema4', color: '#ef5350',
      },
      {
        id: 'builtin_sma1',   name: 'SMA (20)',         description: 'Simple Moving Average',
        type: 'builtin', indicatorKey: 'sma1', color: '#03a9f4',
      },
      {
        id: 'builtin_sma2',   name: 'SMA (50)',         description: 'Simple Moving Average',
        type: 'builtin', indicatorKey: 'sma2', color: '#ff9800',
      },
      {
        id: 'builtin_supertrend', name: 'Supertrend',  description: 'ATR-based dynamic support/resistance',
        type: 'builtin', indicatorKey: 'supertrend', color: '#26a69a',
      },
      {
        id: 'builtin_psar',   name: 'Parabolic SAR',   description: 'Dot-based trailing stop indicator',
        type: 'builtin', indicatorKey: 'psar', color: '#fb923c',
      },
      {
        id: 'builtin_pivots', name: 'Pivot Points',    description: 'Classic daily pivot levels (P, R1-3, S1-3)',
        type: 'builtin', indicatorKey: 'pivots', color: '#fbbf24',
      },
      {
        id: 'builtin_orderblock', name: 'Order Block Detector', description: 'LuxAlgo-style volume-pivot order blocks with mitigation',
        type: 'builtin', indicatorKey: 'orderblock', color: '#089981', isNew: true,
      },
      // Momentum
      {
        id: 'builtin_rsi',    name: 'RSI (14)',         description: 'Relative Strength Index',
        type: 'builtin', indicatorKey: 'rsi', color: '#a78bfa',
      },
      {
        id: 'builtin_macd',   name: 'MACD (12,26,9)',  description: 'Moving Average Convergence Divergence',
        type: 'builtin', indicatorKey: 'macd', color: '#2962ff',
      },
      {
        id: 'builtin_stoch',  name: 'Stochastic',      description: 'Stochastic oscillator %K/%D',
        type: 'builtin', indicatorKey: 'stoch', color: '#22d3ee',
      },
      {
        id: 'builtin_wr',     name: 'Williams %R',     description: 'Williams Percent Range',
        type: 'builtin', indicatorKey: 'williamsR', color: '#06b6d4',
      },
      {
        id: 'builtin_cci',    name: 'CCI (20)',         description: 'Commodity Channel Index',
        type: 'builtin', indicatorKey: 'cci', color: '#f97316',
      },
      // Volatility
      {
        id: 'builtin_bb',     name: 'Bollinger Bands', description: 'Standard deviation bands (20,2)',
        type: 'builtin', indicatorKey: 'bb', color: '#38bdf8',
      },
      {
        id: 'builtin_atr',    name: 'ATR (14)',         description: 'Average True Range — volatility measure',
        type: 'builtin', indicatorKey: 'atr_ind', color: '#f59e0b',
      },
      // Volume
      {
        id: 'builtin_volume', name: 'Volume',           description: 'Bar volume histogram',
        type: 'builtin', indicatorKey: 'volume', color: '#26a69a',
      },
      {
        id: 'builtin_vwap',   name: 'VWAP',             description: 'Volume Weighted Average Price (intraday reset)',
        type: 'builtin', indicatorKey: 'vwap', color: '#fbbf24',
      },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════
//  SOURCE RESOLVER
// ═══════════════════════════════════════════════════════════════════════════
export const getSrc = (c: Candle, src?: IndicatorConfig['source']): number => {
  switch (src) {
    case 'open':  return c.open;
    case 'high':  return c.high;
    case 'low':   return c.low;
    case 'hl2':   return (c.high + c.low) / 2;
    case 'hlc3':  return (c.high + c.low + c.close) / 3;
    default:      return c.close;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
//  TECHNICAL ANALYSIS ENGINE
// ═══════════════════════════════════════════════════════════════════════════
export const TA = {
  sma: (data: Candle[], p: number, src?: IndicatorConfig['source']): (number | null)[] => {
    const out: (number | null)[] = [];
    for (let i = 0; i < data.length; i++) {
      if (i < p - 1) { out.push(null); continue; }
      let s = 0;
      for (let j = i - p + 1; j <= i; j++) s += getSrc(data[j], src);
      out.push(s / p);
    }
    return out;
  },

  ema: (data: Candle[], p: number, src?: IndicatorConfig['source']): (number | null)[] => {
    const out: (number | null)[] = [];
    if (!data.length || p < 1) return out;
    const k = 2 / (p + 1);
    let e = getSrc(data[0], src);
    for (let i = 0; i < data.length; i++) {
      if (i < p - 1) { out.push(null); e = getSrc(data[i], src); continue; }
      if (i === p - 1) {
        let s = 0;
        for (let j = 0; j < p; j++) s += getSrc(data[j], src);
        e = s / p;
      } else {
        e = getSrc(data[i], src) * k + e * (1 - k);
      }
      out.push(e);
    }
    return out;
  },

  vwap: (data: Candle[]): (number | null)[] => {
    const out: (number | null)[] = [];
    let pv = 0, vv = 0, lastDay = -1;
    for (const c of data) {
      const day = Math.floor((c.time + 19800) / 86400);
      if (day !== lastDay) { pv = 0; vv = 0; lastDay = day; }
      const tp = (c.high + c.low + c.close) / 3;
      pv += tp * c.volume; vv += c.volume;
      out.push(vv ? pv / vv : c.close);
    }
    return out;
  },

  rsi: (data: Candle[], p: number = 14): (number | null)[] => {
    const out: (number | null)[] = data.map(() => null);
    if (data.length < p + 1) return out;
    let g = 0, l = 0;
    for (let i = 1; i <= p; i++) {
      const ch = data[i].close - data[i - 1].close;
      if (ch > 0) g += ch; else l -= ch;
    }
    g /= p; l /= p;
    out[p] = l === 0 ? 100 : 100 - 100 / (1 + g / l);
    for (let i = p + 1; i < data.length; i++) {
      const ch = data[i].close - data[i - 1].close;
      const gn = ch > 0 ? ch : 0, ls = ch < 0 ? -ch : 0;
      g = (g * (p - 1) + gn) / p; l = (l * (p - 1) + ls) / p;
      out[i] = l === 0 ? 100 : 100 - 100 / (1 + g / l);
    }
    return out;
  },

  macd: (data: Candle[], fast: number = 12, slow: number = 26, sig: number = 9) => {
    const ef = TA.ema(data, fast), es = TA.ema(data, slow);
    const macd = data.map((_, i) =>
      ef[i] !== null && es[i] !== null ? ef[i]! - es[i]! : null
    );
    const signal: (number | null)[] = [];
    const k = 2 / (sig + 1);
    let s: number | null = null, warm = 0;
    for (let i = 0; i < macd.length; i++) {
      const v = macd[i];
      if (v === null) { signal.push(null); continue; }
      warm++;
      if (warm < sig) { signal.push(null); continue; }
      if (warm === sig) {
        let sum = 0, cnt = 0;
        for (let j = i - sig + 1; j <= i; j++) if (macd[j] !== null) { sum += macd[j]!; cnt++; }
        s = sum / cnt;
      } else { s = v * k + s! * (1 - k); }
      signal.push(s);
    }
    const hist = macd.map((v, i) =>
      v !== null && signal[i] !== null ? v - signal[i]! : null
    );
    return { macd, signal, hist };
  },

  bb: (data: Candle[], p: number = 20, m: number = 2) => {
    const sma = TA.sma(data, p);
    const upper: (number | null)[] = [], lower: (number | null)[] = [];
    for (let i = 0; i < data.length; i++) {
      if (i < p - 1 || sma[i] === null) { upper.push(null); lower.push(null); continue; }
      let ss = 0;
      for (let j = i - p + 1; j <= i; j++) ss += Math.pow(data[j].close - sma[i]!, 2);
      const sd = Math.sqrt(ss / p);
      upper.push(sma[i]! + m * sd);
      lower.push(sma[i]! - m * sd);
    }
    return { upper, middle: sma, lower };
  },

  // Pine ta.atr(p) = ta.rma(ta.tr(true), p): index i = ATR THROUGH bar i, SMA
  // seed at bar p-1. 2026-09-27 zero-drift: this returned n+1 values with a
  // leading null (index i = ATR through bar i-1), one bar behind Pine; the
  // engine (rudra_dna.ta_atr) and the chart-brain changed in the same step.
  atr: (data: Candle[], p: number = 14): (number | null)[] => {
    const out: (number | null)[] = [];
    if (data.length < 2) return out;
    const trArr: number[] = [data[0].high - data[0].low];
    for (let i = 1; i < data.length; i++) {
      const tr = Math.max(
        data[i].high - data[i].low,
        Math.abs(data[i].high - data[i - 1].close),
        Math.abs(data[i].low - data[i - 1].close)
      );
      trArr.push(tr);
    }
    let atr = 0;
    for (let i = 0; i < p; i++) atr += trArr[i];
    atr /= p;
    for (let i = 0; i < data.length; i++) {
      if (i < p - 1) { out.push(null); continue; }
      if (i === p - 1) { out.push(atr); continue; }   // Pine rma seed
      atr = (atr * (p - 1) + trArr[i]) / p;
      out.push(atr);
    }
    return out;
  },

  supertrend: (
    data: Candle[], p: number = 10, mult: number = 3
  ): { trend: (number | null)[]; dir: (boolean | null)[] } => {
    const atr = TA.atr(data, p);
    const trend: (number | null)[] = new Array(data.length).fill(null);
    const dir: (boolean | null)[] = new Array(data.length).fill(null);
    let prevUp = 0, prevDown = 0, prevDir = true;
    for (let i = 1; i < data.length; i++) {
      if (atr[i] === null) continue;
      const hl2 = (data[i].high + data[i].low) / 2;
      let up = hl2 - mult * atr[i]!;
      let down = hl2 + mult * atr[i]!;
      if (prevUp !== 0) up = data[i - 1].close > prevUp ? Math.max(up, prevUp) : up;
      if (prevDown !== 0) down = data[i - 1].close < prevDown ? Math.min(down, prevDown) : down;
      const bullish = prevDir ? data[i].close > prevDown : data[i].close > prevUp;
      trend[i] = bullish ? up : down;
      dir[i] = bullish;
      prevUp = up; prevDown = down; prevDir = bullish;
    }
    return { trend, dir };
  },

  stoch: (data: Candle[], kp: number = 14, ks: number = 3, ds: number = 3) => {
    const rawK: (number | null)[] = [];
    for (let i = 0; i < data.length; i++) {
      if (i < kp - 1) { rawK.push(null); continue; }
      let lo = Infinity, hi = -Infinity;
      for (let j = i - kp + 1; j <= i; j++) {
        lo = Math.min(lo, data[j].low);
        hi = Math.max(hi, data[j].high);
      }
      rawK.push(hi === lo ? 50 : ((data[i].close - lo) / (hi - lo)) * 100);
    }
    const K = TA.sma(rawK.map(v => ({
      open: v ?? 0, high: v ?? 0, low: v ?? 0, close: v ?? 0, volume: 0, time: 0,
    })), ks);
    const D = TA.sma(K.map(v => ({
      open: v ?? 0, high: v ?? 0, low: v ?? 0, close: v ?? 0, volume: 0, time: 0,
    })), ds);
    return { K, D };
  },

  williamsR: (data: Candle[], p: number = 14): (number | null)[] => {
    return data.map((_, i) => {
      if (i < p - 1) return null;
      let lo = Infinity, hi = -Infinity;
      for (let j = i - p + 1; j <= i; j++) {
        lo = Math.min(lo, data[j].low);
        hi = Math.max(hi, data[j].high);
      }
      return hi === lo ? -50 : ((hi - data[i].close) / (hi - lo)) * -100;
    });
  },

  cci: (data: Candle[], p: number = 20): (number | null)[] => {
    return data.map((_, i) => {
      if (i < p - 1) return null;
      const tp = data.slice(i - p + 1, i + 1).map(c => (c.high + c.low + c.close) / 3);
      const mean = tp.reduce((a, b) => a + b, 0) / p;
      const md = tp.reduce((a, b) => a + Math.abs(b - mean), 0) / p;
      return md === 0 ? 0 : (tp[tp.length - 1] - mean) / (0.015 * md);
    });
  },

  psar: (
    data: Candle[], step: number = 0.02, maxStep: number = 0.2
  ): { values: (number | null)[]; bullish: (boolean | null)[] } => {
    const values: (number | null)[] = [null];
    const bullish: (boolean | null)[] = [null];
    if (data.length < 2) return { values, bullish };
    let bull = data[1].close > data[0].close;
    let af = step, ep = bull ? data[0].high : data[0].low;
    let sar = bull ? data[0].low : data[0].high;
    for (let i = 1; i < data.length; i++) {
      sar = sar + af * (ep - sar);
      if (bull) {
        sar = Math.min(sar, data[i - 1].low, i > 1 ? data[i - 2].low : data[i - 1].low);
        if (data[i].low < sar) { bull = false; sar = ep; ep = data[i].low; af = step; }
        else { if (data[i].high > ep) { ep = data[i].high; af = Math.min(af + step, maxStep); } }
      } else {
        sar = Math.max(sar, data[i - 1].high, i > 1 ? data[i - 2].high : data[i - 1].high);
        if (data[i].high > sar) { bull = true; sar = ep; ep = data[i].high; af = step; }
        else { if (data[i].low < ep) { ep = data[i].low; af = Math.min(af + step, maxStep); } }
      }
      values.push(sar);
      bullish.push(bull);
    }
    return { values, bullish };
  },

  pivots: (
    data: Candle[]
  ): { P: number; R1: number; R2: number; R3: number; S1: number; S2: number; S3: number } | null => {
    if (data.length < 2) return null;
    const prev = data[data.length - 2];
    // FIBONACCI pivots (God 2026-10-30): P = (H+L+C)/3, R/S n = P +/- {0.382, 0.618, 1.000} x (H - L)
    const P = (prev.high + prev.low + prev.close) / 3;
    const rg = prev.high - prev.low;
    const R1 = P + 0.382 * rg; const S1 = P - 0.382 * rg;
    const R2 = P + 0.618 * rg; const S2 = P - 0.618 * rg;
    const R3 = P + rg;         const S3 = P - rg;
    return { P, R1, R2, R3, S1, S2, S3 };
  },

  // ── Order Block Detector (LuxAlgo method) ─────────────────────────────────
  // Volume pivot highs (strict max over ±length) mark institutional activity.
  // Market-structure oscillator decides side: price below the rolling range →
  // bullish OB (demand), above → bearish OB (supply). A block stays active
  // until price MITIGATES it (wick or close through the far edge), then it is
  // greyed. Returns the last N active + recently-mitigated blocks per side.
  orderBlocksLux: (
    data: Candle[],
    opts: { pivotLen?: number; bullCount?: number; bearCount?: number; mitigation?: 'Wick' | 'Close' },
  ): {
    bull: { startIdx: number; top: number; btm: number; avg: number; mitigatedAt: number | null }[];
    bear: { startIdx: number; top: number; btm: number; avg: number; mitigatedAt: number | null }[];
  } => {
    const L = Math.max(2, opts.pivotLen ?? 12);
    const nBull = Math.max(1, opts.bullCount ?? 2);
    const nBear = Math.max(1, opts.bearCount ?? 2);
    const useWick = (opts.mitigation ?? 'Wick') === 'Wick';
    const n = data.length;
    type OB = { startIdx: number; top: number; btm: number; avg: number; mitigatedAt: number | null };
    const bull: OB[] = [], bear: OB[] = [];
    if (n < L * 2 + 2) return { bull, bear };

    // market-structure oscillator (LuxAlgo `os`): 0 = bullish structure, 1 = bearish
    let os = 0;
    const osArr = new Int8Array(n);
    for (let i = 0; i < n; i++) {
      if (i >= L) {
        let hh = -Infinity, ll = Infinity;
        for (let j = i - L + 1; j <= i; j++) { hh = Math.max(hh, data[j].high); ll = Math.min(ll, data[j].low); }
        const ref = data[i - L];
        if (ref.high > hh) os = 0;
        else if (ref.low < ll) os = 1;
      }
      osArr[i] = os;
    }

    for (let i = L; i < n - L; i++) {
      // strict volume pivot high at i
      let isPivot = data[i].volume > 0;
      for (let j = i - L; j <= i + L && isPivot; j++) {
        if (j !== i && data[j].volume >= data[i].volume) isPivot = false;
      }
      if (!isPivot) continue;
      const c = data[i];
      const hl2 = (c.high + c.low) / 2;
      if (osArr[i + L] === 1) {
        bull.push({ startIdx: i, top: hl2, btm: c.low, avg: (hl2 + c.low) / 2, mitigatedAt: null });
      } else {
        bear.push({ startIdx: i, top: c.high, btm: hl2, avg: (c.high + hl2) / 2, mitigatedAt: null });
      }
    }

    // mitigation scan
    for (const ob of bull) {
      for (let i = ob.startIdx + 1; i < n; i++) {
        const probe = useWick ? data[i].low : Math.min(data[i].open, data[i].close);
        if (probe < ob.btm) { ob.mitigatedAt = i; break; }
      }
    }
    for (const ob of bear) {
      for (let i = ob.startIdx + 1; i < n; i++) {
        const probe = useWick ? data[i].high : Math.max(data[i].open, data[i].close);
        if (probe > ob.top) { ob.mitigatedAt = i; break; }
      }
    }

    // keep last N active + last N mitigated per side (newest first)
    const pick = (arr: OB[], keep: number) => {
      const active = arr.filter(o => o.mitigatedAt === null).slice(-keep);
      const mit = arr.filter(o => o.mitigatedAt !== null).slice(-keep);
      return [...mit, ...active];
    };
    return { bull: pick(bull, nBull), bear: pick(bear, nBear) };
  },

  // ── TradingView-parity Pivot Points ──────────────────────────────────────
  // Groups candles into pivot periods (Daily/Weekly/Monthly, IST), computes the
  // chosen formula from each PREVIOUS period's OHLC, and returns one set per
  // period with the bar range it applies to — so the chart can draw TV-style
  // per-period segments instead of one full-width line.
  pivotsTV: (
    data: Candle[],
    opts: { type?: string; tf?: 'Daily' | 'Weekly' | 'Monthly'; back?: number;
            /** God 31-Oct audit: period id (IST day / Monday week) -> full-period H/L/C from the server's 1D bars.
             *  When given, a period's pivots come from the latest map period BEFORE it - never from a loaded
             *  window that starts mid-day (1s = 2000 bars = 33 min). A period with no previous map entry gets none. */
            hlc?: Record<number, { h: number; l: number; c: number }> | null },
  ): { startIdx: number; endIdx: number; levels: Record<string, number> }[] => {
    if (data.length < 2) return [];
    const type = opts.type || 'Fibonacci';
    const tf = opts.tf || 'Daily';
    const back = Math.max(1, Math.min(60, opts.back ?? 15));

    const periodOf = (t: number): number => {
      const day = Math.floor((t + 19_800) / 86_400);          // IST day index
      if (tf === 'Daily') return day;
      if (tf === 'Weekly') return Math.floor((day + 3) / 7);  // Monday-start weeks
      const d = new Date((t + 19_800) * 1000);
      return d.getUTCFullYear() * 12 + d.getUTCMonth();       // Monthly
    };

    // contiguous period segments: {period, startIdx, endIdx, O,H,L,C}
    const segs: { p: number; s: number; e: number; o: number; h: number; l: number; c: number }[] = [];
    for (let i = 0; i < data.length; i++) {
      const p = periodOf(data[i].time);
      const last = segs[segs.length - 1];
      if (!last || last.p !== p) {
        segs.push({ p, s: i, e: i, o: data[i].open, h: data[i].high, l: data[i].low, c: data[i].close });
      } else {
        last.e = i;
        last.h = Math.max(last.h, data[i].high);
        last.l = Math.min(last.l, data[i].low);
        last.c = data[i].close;
      }
    }
    if (segs.length < 2 && !opts.hlc) return [];

    const calc = (H: number, L: number, C: number, O2: number): Record<string, number> => {
      const range = H - L;
      switch (type) {
        case 'Fibonacci': {
          const P = (H + L + C) / 3;
          return { P, R1: P + 0.382 * range, R2: P + 0.618 * range, R3: P + range,
                   S1: P - 0.382 * range, S2: P - 0.618 * range, S3: P - range };
        }
        case 'Woodie': {
          const P = (H + L + 2 * O2) / 4;
          return { P, R1: 2 * P - L, R2: P + range, R3: H + 2 * (P - L), R4: H + 2 * (P - L) + range,
                   S1: 2 * P - H, S2: P - range, S3: L - 2 * (H - P), S4: L - 2 * (H - P) - range };
        }
        case 'Classic': {
          const P = (H + L + C) / 3;
          const out: Record<string, number> = { P };
          for (let n = 1; n <= 4; n++) {
            out[`R${n}`] = n === 1 ? 2 * P - L : P + (n - 1) * range;
            out[`S${n}`] = n === 1 ? 2 * P - H : P - (n - 1) * range;
          }
          return out;
        }
        case 'Camarilla': {
          const k = 1.1;
          return { P: (H + L + C) / 3,
                   R1: C + k * range / 12, R2: C + k * range / 6, R3: C + k * range / 4,
                   R4: C + k * range / 2, R5: (H / L) * C,
                   S1: C - k * range / 12, S2: C - k * range / 6, S3: C - k * range / 4,
                   S4: C - k * range / 2, S5: C - ((H / L) * C - C) };
        }
        case 'DM': {
          const X = C < O2 ? H + 2 * L + C : C > O2 ? 2 * H + L + C : H + L + 2 * C;
          return { P: X / 4, R1: X / 2 - L, S1: X / 2 - H };
        }
        default: {  // Traditional (TV default)
          const P = (H + L + C) / 3;
          const out: Record<string, number> = { P, R1: 2 * P - L, S1: 2 * P - H,
                                                R2: P + range, S2: P - range };
          for (let n = 3; n <= 5; n++) {
            out[`R${n}`] = P * (n - 1) + (H - (n - 1) * L);
            out[`S${n}`] = P * (n - 1) - ((n - 1) * H - L);
          }
          return out;
        }
      }
    };

    const out: { startIdx: number; endIdx: number; levels: Record<string, number> }[] = [];
    if (opts.hlc) {
      const keys = Object.keys(opts.hlc).map(Number).sort((a, b) => a - b);
      for (let i = Math.max(0, segs.length - back); i < segs.length; i++) {
        const cur = segs[i];
        let pk = -Infinity;
        for (const k of keys) { if (k < cur.p) pk = k; else break; }
        const prev = Number.isFinite(pk) ? opts.hlc[pk] : null;
        if (prev && prev.h > 0 && prev.l > 0) out.push({ startIdx: cur.s, endIdx: cur.e, levels: calc(prev.h, prev.l, prev.c, cur.o) });
      }
      return out;
    }
    const from = Math.max(1, segs.length - back);
    for (let i = from; i < segs.length; i++) {
      const prev = segs[i - 1], cur = segs[i];
      out.push({ startIdx: cur.s, endIdx: cur.e, levels: calc(prev.h, prev.l, prev.c, cur.o) });
    }
    return out;
  },
};

// ─── TV Color presets (used by TVIndicatorModal) ─────────────────────────────
export const TV_PRESET_COLORS = [
  '#2962ff', '#e91e63', '#ff9800', '#00e676',
  '#9c27b0', '#26a69a', '#ff6d00', '#1de9b6',
  '#fbbf24', '#ef4444', '#22d3ee', '#64748b',
];

// ─── Visibility timeframe keys (used by TVIndicatorModal) ────────────────────
export const TV_VIS_TFS = [
  { key: 'visSec',  label: 'Seconds', max: 59  },
  { key: 'visMin',  label: 'Minutes', max: 59  },
  { key: 'visHour', label: 'Hours',   max: 24  },
  { key: 'visDay',  label: 'Days',    max: 366 },
  { key: 'visWeek', label: 'Weeks',   max: 52  },
  { key: 'visMon',  label: 'Months',  max: 12  },
];